import { ClientService, ToastMessageService } from '@fitness-force/api';
import { DatePipe } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  input,
  output,
  effect,
  signal,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  Validators,
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidatorFn,
  ValidationErrors,
  AbstractControl,
} from '@angular/forms';
import { ErrorMsgComponent } from '@fitness-force/errors';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { DeletionDetails } from '@fitness-force/models';
import { DeletionRequestStatus } from '@fitness-force/enum';
import { AvatarModule } from 'primeng/avatar';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { BehaviorSubject } from 'rxjs';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';

@Component({
  selector: 'app-delete-client',
  templateUrl: './delete-client.component.html',
  styleUrls: ['./delete-client.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    AvatarModule,
    ButtonModule,
    RippleModule,
    BlockUIModule,
    StyleClassModule,
    InputTextModule,
    TextareaModule,
    ShortNamePipe,
    TranslateModule,
    ErrorMsgComponent,
    DatePipe,
  ],
})
export class DeleteClientComponent implements OnInit {
  readonly deleteClientModal = input<boolean | null>(false);
  readonly clientData$ = input<any>(new BehaviorSubject(null));
  readonly closeDeleteClientDialog = output<{
    close: boolean;
    action: string | null;
  }>();

  deleteClientFormGroup: UntypedFormGroup = new UntypedFormGroup({});

  // Angular 21: Use signals for component state
  allClientData = signal<any>(null);
  btnLoading = signal(false);
  hasPendingRequest = signal(false);
  pendingDeletionRequest = signal<DeletionDetails | null>(null);
  private readonly isClosing = signal(false);

  constructor(
    private readonly fb: UntypedFormBuilder,
    readonly translate: TranslateService,
    private readonly clientService: ClientService,
    private readonly toastMessageService: ToastMessageService,
  ) {
    // Angular 21: Use effect in constructor to watch for dialog visibility changes
    effect(() => {
      const isVisible = this.deleteClientModal();
      if (!isVisible) {
        this.isClosing.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.componentInit();
  }

  componentInit() {
    const clientDataSubject = this.clientData$();
    if (clientDataSubject) {
      clientDataSubject.subscribe((client: any) => {
        this.allClientData.set(client);

        // Check for pending deletion request
        this.checkPendingDeletionRequest();

        // Initialize form after we have client data
        this.deleteClientFormGroup = this.fb.group({
          confirmationText: [
            '',
            [Validators.required, this.deleteConfirmationValidator()],
          ],
          clientName: [client?.full_name || ''],
          reason: ['', Validators.required],
        });
      });
    }
  }

  /**
   * Check if there's a pending deletion request within 30 days
   */
  checkPendingDeletionRequest() {
    const clientData = this.allClientData();
    const deletionDetails = clientData?.deletion
      ? (JSON.parse(clientData.deletion) as DeletionDetails[])
      : [];
    if (
      !deletionDetails ||
      !Array.isArray(deletionDetails) ||
      deletionDetails.length === 0
    ) {
      this.hasPendingRequest.set(false);
      this.pendingDeletionRequest.set(null);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() - 30);
    thirtyDaysFromNow.setHours(23, 59, 59, 999);

    // Find the most recent pending deletion request within the date range
    const pendingRequests = deletionDetails.filter(
      (deletion: DeletionDetails) => {
        if (deletion.request_status !== DeletionRequestStatus.Pending) {
          return false;
        }

        const requestedDate = new Date(deletion.requested_date);
        return requestedDate >= thirtyDaysFromNow && requestedDate <= today;
      },
    );

    if (pendingRequests.length > 0) {
      // Get the most recent one - sort in place then take first
      const sortedRequests = [...pendingRequests].sort(
        (a: DeletionDetails, b: DeletionDetails) =>
          new Date(b.requested_date).getTime() -
          new Date(a.requested_date).getTime(),
      );
      this.pendingDeletionRequest.set(sortedRequests[0]);
      this.hasPendingRequest.set(true);
    } else {
      this.hasPendingRequest.set(false);
      this.pendingDeletionRequest.set(null);
    }
  }

  /**
   * Custom validator for delete confirmation
   * User must type the exact client name to confirm deletion
   */
  deleteConfirmationValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const clientData = this.allClientData();
      if (!control.value || !clientData?.full_name) {
        return null;
      }

      const inputValue = this.normalizeConfirmationName(control.value);
      const clientName = this.normalizeConfirmationName(clientData.full_name);

      return inputValue === clientName ? null : { wrongText: true };
    };
  }

  private normalizeConfirmationName(name: string): string {
    return name
      .normalize('NFKC')
      .toLowerCase()
      .replace(/[\s\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000\uFEFF]+/g, ' ')
      .trim();
  }

  onDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closeDialogEvent();
    }
  }

  closeDialogEvent(action: string | null = null) {
    // Angular 21: Use signals - prevent multiple close events from being emitted
    // Only emit if dialog is actually visible
    const isVisible = this.deleteClientModal();
    if (this.isClosing() || !isVisible) {
      return;
    }
    this.isClosing.set(true);
    this.closeDeleteClientDialog.emit({ close: true, action });
    // The effect in constructor will reset isClosing when dialog closes
  }

  deleteClient() {
    if (this.deleteClientFormGroup.invalid) {
      this.deleteClientFormGroup.markAllAsTouched();
      return;
    }

    this.btnLoading.set(true);

    const clientData = this.allClientData();
    let tenantId = 0;
    if (clientData?.home_club_tenant_id && clientData.home_club_tenant_id > 0) {
      tenantId = clientData.home_club_tenant_id;
    } else {
      tenantId = clientData?.createdin_tenant_id || 0;
    }

    // Prepare payload
    const pendingRequest = this.pendingDeletionRequest();
    const payload = {
      request_id: pendingRequest?.request_id || null,
      reason: pendingRequest?.reason || this.Reason.value,
    };

    this.clientService
      .deleteClient(clientData.id, tenantId, payload)
      .subscribe({
        next: () => {
          this.translate
            .get('CLIENT_PROFILE.DELETE_CLIENT.DELETED_SUCCESS')
            .subscribe((translations: any) => {
              this.toastMessageService.showSuccess(translations);
            });
          this.btnLoading.set(false);
          this.closeDialogEvent('deleted');
          // Angular 21: No need for manual change detection with signals
        },
        error: (error) => {
          this.btnLoading.set(false);
          this.toastMessageService.showError(error);
          // Angular 21: No need for manual change detection with signals
        },
      });
  }

  get ConfirmationText(): UntypedFormControl {
    return this.deleteClientFormGroup.get(
      'confirmationText',
    ) as UntypedFormControl;
  }

  get ClientName(): UntypedFormControl {
    return this.deleteClientFormGroup.get('clientName') as UntypedFormControl;
  }

  get Reason(): UntypedFormControl {
    return this.deleteClientFormGroup.get('reason') as UntypedFormControl;
  }
}
